import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronRight, Check, Plus, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, PRODUCTS, BRAND } from "@/data/products";
import { useQuote } from "@/context/QuoteContext";

const setMetaDescription = (desc) => {
  let m = document.querySelector('meta[name="description"]');
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute("name", "description");
    document.head.appendChild(m);
  }
  m.setAttribute("content", desc);
};

const setMetaKeywords = (kw) => {
  let m = document.querySelector('meta[name="keywords"]');
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute("name", "keywords");
    document.head.appendChild(m);
  }
  m.setAttribute("content", kw);
};

const ProductDetailPage = () => {
  const { slug, productSlug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useQuote();

  const category = CATEGORIES.find((c) => c.slug === slug);
  const product = useMemo(
    () => (PRODUCTS[slug] || []).find((p) => p.slug === productSlug),
    [slug, productSlug]
  );

  const [activeImage, setActiveImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(product?.sizes?.[0] || "");

  useEffect(() => {
    if (product?.seo) {
      const prevTitle = document.title;
      document.title = product.seo.title;
      setMetaDescription(product.seo.description);
      if (product.seo.keywords) setMetaKeywords(product.seo.keywords);
      return () => { document.title = prevTitle; };
    }
  }, [product]);

  useEffect(() => { window.scrollTo(0, 0); }, [productSlug]);

  if (!category || !product) {
    return (
      <main className="max-w-7xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif-display text-3xl">Product not found</h1>
        <Link to="/products/platform-bed-frames" className="text-[#D4AF37] underline mt-4 inline-block">
          Back to Bed Frames
        </Link>
      </main>
    );
  }

  const images = product.images || [product.image];

  const handleAddToQuote = () => {
    const variantName = selectedSize ? `${product.name} · ${selectedSize}` : product.name;
    addItem(
      { id: `${product.id}-${selectedSize || "default"}`, name: variantName, image: images[0] },
      category.name
    );
    toast.success(`${variantName} added to your quote`);
  };

  return (
    <main data-testid={`product-detail-${product.slug}`} className="bg-[#FAF9F6]">
      {/* Breadcrumb */}
      <div className="border-b border-[#D4AF37]/20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#4A4A4A]">
          <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/products/${category.slug}`} className="hover:text-[#D4AF37]">{category.name}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#D4AF37] truncate">{product.name}</span>
        </div>
      </div>

      {/* Product hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="relative bg-white border border-[#E5E5E5] aspect-[4/3] overflow-hidden">
            <img
              key={activeImage}
              data-testid="product-main-image"
              src={images[activeImage]}
              alt={product.name}
              className="w-full h-full object-contain hh-fade-in"
            />
          </div>
          {images.length > 1 && (
            <div className="mt-4 grid grid-cols-4 gap-3">
              {images.map((img, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActiveImage(i)}
                  className={`aspect-square overflow-hidden border transition-all ${
                    i === activeImage ? "border-[#D4AF37]" : "border-[#E5E5E5] hover:border-[#D4AF37]/60"
                  }`}
                >
                  <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-contain bg-white" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">{category.name}</p>
          <h1 data-testid="product-title" className="font-serif-display text-4xl lg:text-5xl mt-4 leading-[1.1]">
            {product.name}
          </h1>
          <p className="mt-6 text-[#4A4A4A] leading-relaxed">{product.shortDescription}</p>

          {/* Size selector */}
          {product.sizes && product.sizes.length > 0 && (
            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A] mb-3">Select Size</p>
              <div className="flex flex-wrap gap-3" data-testid="size-selector">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-option-${s.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setSelectedSize(s)}
                    className={`px-5 py-3 text-sm uppercase tracking-[0.15em] border transition-colors ${
                      selectedSize === s
                        ? "border-[#D4AF37] bg-[#D4AF37] text-white"
                        : "border-[#E5E5E5] text-[#1A1A1A] hover:border-[#D4AF37]"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Color */}
          {product.color && (
            <div className="mt-6 flex items-center gap-4 flex-wrap">
              <p className="text-xs uppercase tracking-[0.2em] text-[#1A1A1A]">Color</p>
              <div className="flex items-center gap-3" data-testid="color-swatches">
                {(product.colorSwatches || [{ label: product.color, value: "#000000" }]).map((c) => (
                  <div key={c.label} className="flex items-center gap-2">
                    <span
                      className="inline-block w-5 h-5 rounded-full border border-[#E5E5E5]"
                      style={{ background: c.value }}
                      aria-hidden
                    />
                    <span className="text-sm text-[#1A1A1A]">{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap gap-3">
            <button
              data-testid="detail-add-to-quote"
              onClick={handleAddToQuote}
              className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B5952F] text-white px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
            >
              <Plus className="w-4 h-4" /> Add to Quote
            </button>
            <Link
              to="/contact"
              data-testid="detail-inquire-btn"
              className="inline-flex items-center gap-2 border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
            >
              <Mail className="w-4 h-4" /> Request Custom Quote
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-6 text-xs text-[#4A4A4A] uppercase tracking-[0.18em] border-t border-[#D4AF37]/20 pt-6">
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> 5-Year Warranty</span>
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> Factory Direct</span>
            <span className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#D4AF37]" /> Nationwide Shipping</span>
          </div>

          <div className="mt-6 text-sm text-[#4A4A4A]">
            Talk to a rep: <a href={`tel:${BRAND.phone}`} className="text-[#D4AF37] hover:underline">{BRAND.phone}</a>
          </div>
        </div>
      </section>

      {/* Key features */}
      {product.features && (
        <section className="bg-white border-y border-[#D4AF37]/20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
            <div className="mb-10">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Specifications</p>
              <h2 className="font-serif-display text-3xl lg:text-4xl mt-3">Key Features</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-0">
              {product.features.map((f, i) => (
                <div
                  key={f.label}
                  data-testid={`feature-${f.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-baseline justify-between py-5 border-b border-[#E5E5E5] ${i === product.features.length - 1 || i === product.features.length - 2 ? "" : ""}`}
                >
                  <span className="text-xs uppercase tracking-[0.2em] text-[#4A4A4A]">{f.label}</span>
                  <span className="font-serif-display text-lg text-[#1A1A1A] text-right ml-6">{f.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Detailed description */}
      {product.longDescription && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Overview</p>
            <h2 className="font-serif-display text-3xl lg:text-4xl mt-3 leading-tight">
              Built for hospitality. Engineered to last.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-[#4A4A4A] leading-relaxed text-lg">
            {product.longDescription.map((p, i) => (
              <p key={i} data-testid={`long-desc-${i}`}>{p}</p>
            ))}
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="bg-[#1A1A1A] text-white p-10 md:p-14 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-serif-display text-3xl lg:text-4xl leading-tight">Bulk pricing for your property?</h3>
            <p className="text-white/70 mt-3">Get factory-direct pricing on 20+ units. Our team responds within 24 hours.</p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <button
              onClick={handleAddToQuote}
              data-testid="bottom-cta-add-quote"
              className="bg-[#D4AF37] hover:bg-[#B5952F] text-white px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
            >
              Add to Quote
            </button>
            <a
              href={`tel:${BRAND.phone}`}
              data-testid="bottom-cta-call"
              className="inline-flex items-center gap-2 border border-white/40 hover:bg-white hover:text-[#1A1A1A] px-8 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
            >
              <Phone className="w-4 h-4" /> Call Us
            </a>
          </div>
        </div>
      </section>

      {/* Related in same category */}
      <section className="bg-white border-t border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">More from</p>
          <h3 className="font-serif-display text-3xl mb-8">{category.name}</h3>
          <button
            onClick={() => navigate(`/products/${category.slug}`)}
            data-testid="back-to-category-btn"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#1A1A1A] hover:text-[#D4AF37]"
          >
            Browse the full collection <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    </main>
  );
};

export default ProductDetailPage;
